/** Draw a shareable portrait card (1080x1350) for a name, for family group chats. */
import type { CountryId } from "../data/types";
import { native } from "../state/app";
import { count } from "../state/telemetry";
import { saveFile } from "./saveFile";

/** the line under the logo at the foot of a card */
export const TAGLINE: Record<CountryId, string> = {
  uk: "UK baby names from the official records",
  us: "US baby names from the Social Security records",
  other: "Baby names from the UK and US records",
};

const PALETTE = {
  paper: "#f1ecfa",
  surface: "#ffffff",
  ink: "#2a2433",
  ink2: "#5b5368",
  line: "#e9e3f2",
  girl: "#d4628c",
  boy: "#3f7fcc",
  unisex: "#8a6ccc",
};
/** the same colours dark enough for words */
const INK = {
  girl: "#b03a68",
  boy: "#2c66ad",
  unisex: "#6b4fb8",
};

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const next = line ? `${line} ${w}` : w;
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else line = next;
  }
  if (line) lines.push(line);
  return lines;
}

export interface CardContent {
  name: string;
  gender: "girl" | "boy" | "unisex";
  label: string;
  fullName?: string;
  meaning?: string;
  facts: string[];
  /** the line under the logo, e.g. "UK baby names from the official records" */
  tagline: string;
  /** news above the name in place of the girl/boy label: "We've chosen", "Welcome" */
  heading?: string;
  /** where Nameling is, under the logo, so anyone who sees the picture can find it */
  site?: string;
}

export async function drawNameCard(c: CardContent): Promise<Blob> {
  const W = 1080;
  const H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable");
  try {
    await document.fonts.load('500 200px "Newsreader"');
  } catch {
    /* falls back to the serif stack */
  }
  const accent = PALETTE[c.gender];
  const serif = '"Newsreader", Georgia, serif';
  const sans = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

  ctx.fillStyle = PALETTE.paper;
  ctx.fillRect(0, 0, W, H);

  // the card: plain white with a hairline edge, like the one in the app
  ctx.save();
  ctx.shadowColor = "rgba(60, 40, 110, 0.12)";
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 12;
  roundRect(ctx, 90, 110, W - 180, H - 260, 44);
  ctx.fillStyle = PALETTE.surface;
  ctx.fill();
  ctx.restore();
  roundRect(ctx, 90, 110, W - 180, H - 260, 44);
  ctx.strokeStyle = PALETTE.line;
  ctx.lineWidth = 2;
  ctx.stroke();

  if (c.heading) {
    // news: a line in the name's colour, centred above it
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    ctx.font = `italic 400 66px ${serif}`;
    ctx.fillStyle = INK[c.gender];
    ctx.fillText(c.heading, W / 2, 245);
    ctx.textAlign = "start";
  } else {
    // gender: a dot and a word
    ctx.textBaseline = "middle";
    ctx.beginPath();
    ctx.arc(166, 200, 11, 0, Math.PI * 2);
    ctx.fillStyle = accent;
    ctx.fill();
    ctx.font = `600 34px ${sans}`;
    ctx.fillStyle = PALETTE.ink2;
    ctx.fillText(c.label, 192, 202);
  }

  // the name, as large as fits
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  let size = 230;
  do {
    ctx.font = `500 ${size}px ${serif}`;
    size -= 6;
  } while (ctx.measureText(c.name).width > W - 300 && size > 60);
  const nameFont = ctx.font;

  // centre the name block in the space between the label and the facts
  ctx.font = `400 44px ${sans}`;
  const meaningLines = c.meaning ? wrap(ctx, `“${c.meaning}”`, W - 360).slice(0, 2) : [];
  const blockH = size * 0.78 + (c.fullName ? 100 : 0) + meaningLines.length * 60 + (meaningLines.length ? 20 : 0);
  let y = 270 + (920 - 270 - blockH) / 2 + size * 0.75;

  ctx.font = nameFont;
  ctx.fillStyle = PALETTE.ink;
  ctx.fillText(c.name, W / 2, y);
  y += 100;
  if (c.fullName) {
    ctx.font = `400 54px ${serif}`;
    ctx.fillStyle = PALETTE.ink2;
    ctx.fillText(c.fullName, W / 2, y);
    y += 100;
  }
  if (meaningLines.length) {
    ctx.font = `400 44px ${sans}`;
    ctx.fillStyle = PALETTE.ink2;
    for (const line of meaningLines) {
      ctx.fillText(line, W / 2, y);
      y += 60;
    }
  }

  // facts at the foot of the card
  if (c.facts.length) {
    ctx.strokeStyle = PALETTE.line;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(170, 960);
    ctx.lineTo(W - 170, 960);
    ctx.stroke();
    ctx.font = `600 38px ${sans}`;
    ctx.fillStyle = PALETTE.ink;
    c.facts.slice(0, 3).forEach((f, i) => ctx.fillText(f, W / 2, 1035 + i * 60));
  }

  // footer
  ctx.font = `500 48px ${serif}`;
  ctx.fillStyle = PALETTE.ink;
  ctx.fillText("Nameling", W / 2, c.site ? 1258 : 1270);
  ctx.font = `500 26px ${sans}`;
  ctx.fillStyle = PALETTE.ink2;
  ctx.fillText(c.tagline, W / 2, c.site ? 1298 : 1312);
  if (c.site) {
    ctx.font = `600 26px ${sans}`;
    ctx.fillStyle = INK[c.gender];
    ctx.fillText(c.site.replace(/^https?:\/\//, ""), W / 2, 1334);
  }

  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png"));
}

/** Share the picture where the device can; otherwise save it. `event` is what the usage counts call it. */
export async function shareNameCard(c: CardContent, text: string, event = "share.picture"): Promise<"shared" | "saved" | "none"> {
  const blob = await drawNameCard(c);
  count(event);
  const filename = `${c.heading ? (c.fullName ?? c.name) : c.name}.png`;
  // the iOS app's share sheet has Save Image as well as Messages and the rest
  if (native.shareFile) return (await native.shareFile(filename, blob)) ? "shared" : "none";
  const file = new File([blob], filename, { type: "image/png" });
  if (!__PREVIEW__ && navigator.canShare?.({ files: [file] })) {
    await navigator.share({ files: [file], title: c.name, text });
    return "shared";
  }
  return (await saveFile(file.name, blob)) ? "saved" : "none";
}
